import React, { useState, useEffect, useRef } from "react";
import Dropdown from "../../../components/ui/dropdown";
import Input from "../../../components/ui/input";
import Button from "../../../components/ui/button";
import Table from "../../../components/ui/table";
import Checkbox from "../../../components/ui/checkbox";
import styles from "./appointments.module.css";
import FormSection from "../../../components/screens/form-section";
import { MyIcons } from "../../../components/icons/my-icons";
import { showSuccessToast, showErrorToast, notifications } from "../../../utils/notifications";
import {
  getGuilds,
  getActiveWorkersByGuild,
  getAppointmentSummary,
  getAppointmentById,
  getContactsByExpedienteId,
  createAppointment,
  updateAppointment,
  getBudgetLinesSummary,
  getBudgetLineForAppointment,
  sendAppointment,
} from "../../../services/apiService";
import { useOutletContext, useParams } from "react-router-dom";
import { format } from "date-fns";
import { addHours } from "date-fns";
import DateTimePicker from "../../../components/ui/date-time-picker";
import BudgetLineSelectionModal from "../../../components/modals/BudgetLineSelectionModal";
import SendAppointmentModal from "../../../components/modals/SendAppointmentModal";
import SambaFilePickerModal from "../../../components/modals/SambaFilePickerModal";
import UnsavedChangesModal from "../../../components/modals/unsaved-changes-modal";
import usePageTitle from "../../../hooks/usePageTitle";
import useUnsavedChangesWarning from "../../../hooks/useUnsavedChangesWarning";

// Helper: convert Date -> ISO (or null) - mantiene la hora local sin ajuste de zona horaria
const convertLocalToISO = (date) => {
  if (!date) return null;
  try {
    // Ajustar el offset de zona horaria para mantener la hora local en el ISO
    const offset = date.getTimezoneOffset();
    const adjusted = new Date(date.getTime() - (offset * 60 * 1000));
    return adjusted.toISOString();
  } catch (e) {
    return null;
  }
};

// Helper: convert ISO -> Date (or null)
const convertISOToLocal = (isoString) => {
  if (!isoString) return null;
  try {
    return new Date(isoString);
  } catch (e) {
    return null;
  }
};



const Appointments = () => {
  const { id: expedienteId } = useParams();
  usePageTitle(`Expediente ${expedienteId}`, 'Citas');
  const [formData, setFormData] = useState({
    guild: null,
    worker: null,
    appointmentDate: null,
    endDate: null,
    closedDate: null,
    appointmentType: null,
    contacts: [],
    budgetNumber: "",
    workforce: "",
    materialWorker: "",
    material: "",
    information: "",
    observaciones: "",
    attachedFiles: [],
    cargarOperario: false,
  });
  const { recordData, isLockedByOther, refetchStatistics } = useOutletContext();
  console.log("Record Data:", recordData);
  const [appointmentsTableData, setAppointmentsTableData] = useState([]);
  const [guildOptions, setGuildOptions] = useState([]);
  const [workerOptions, setWorkerOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [contactsOptions, setContactsOptions] = useState([]);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isBudgetLineModalOpen, setIsBudgetLineModalOpen] = useState(false);
  const [budgetLines, setBudgetLines] = useState([]);
  const [isSendModalOpen, setIsSendModalOpen] = useState(false);
  const [selectedAppointmentId, setSelectedAppointmentId] = useState(null);
  const [isCancelled, setIsCancelled] = useState(false);
  const [isReserved, setIsReserved] = useState(false);
  const [isUrgent, setIsUrgent] = useState(false);
  const [isSambaOpen, setIsSambaOpen] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [sinHora, setSinHora] = useState(false);
  const appointmentRef = useRef(null);
  const endRef = useRef(null);
  const closedRef = useRef(null);
  const leftColumnRef = useRef(null);
  const obsContainerRef = useRef(null);

  // Estados para rastrear cambios sin guardar
  const [initialFormData, setInitialFormData] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Hook para advertir sobre cambios sin guardar
  const { showModal, handleCancel, handleConfirm, message } = useUnsavedChangesWarning(
    hasUnsavedChanges,
    '¿Estás seguro de que quieres salir? Los cambios no guardados en la cita se perderán.'
  );

  // Debug modal state changes
  useEffect(() => {
    console.log("Send modal state changed:", { isSendModalOpen, selectedAppointmentId });
  }, [isSendModalOpen, selectedAppointmentId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleDateChange = (name, date) => {
    let processedDate = date;
    
    // Si 'Sin hora' está marcado, ajustar la fecha a las horas específicas
    if (sinHora && processedDate) {
      const dateOnly = new Date(processedDate);
      dateOnly.setHours(0, 0, 0, 0);
      
      if (name === "appointmentDate") {
        // Fecha cita a las 00:00
        processedDate = dateOnly;
      } else if (name === "endDate") {
        // Fecha fin a las 01:00
        dateOnly.setHours(1, 0, 0, 0);
        processedDate = dateOnly;
      }
    }
    
    const updated = { ...formData, [name]: processedDate };
    // Si cambiamos la fecha de inicio y el tipo de cita es Visita, establecer fecha fin +1h
    if (name === "appointmentDate" && processedDate && formData.appointmentType?.value === "Visita" && !sinHora) {
      try {
        updated.endDate = addHours(processedDate, 1);
      } catch (e) {
        console.error("Error adding hour to appointment date:", e);
      }
    } else if (name === "appointmentDate" && processedDate && sinHora) {
      // Si 'Sin hora' está marcado, establecer fecha fin al día siguiente a las 01:00
      const nextDay = new Date(processedDate);
      nextDay.setDate(nextDay.getDate() + 1);
      nextDay.setHours(1, 0, 0, 0);
      updated.endDate = nextDay;
    }
    setFormData(updated);
  };

  const handleDropdownSelect = (field, option) => {
    let updatedFormData = { ...formData, [field]: option };
    
    // Solo copiar el servicio si el tipo de cita es "Visita" y recordData?.servicio
    if (field === "appointmentType" && option && option.value === "Visita" && recordData?.servicio) {
      updatedFormData = { ...updatedFormData, information: recordData.servicio };
    }

    // Si cambio el tipo de cita a "Visita" y ya hay fecha seleccionada (Date), actualizar fecha fin
    if (field === "appointmentType" && option && option.value === "Visita" && formData.appointmentDate) {
      try {
        const newEnd = addHours(formData.appointmentDate, 1);
        updatedFormData = { ...updatedFormData, endDate: newEnd };
      } catch (e) {
        console.error("Error setting end date after changing appointment type:", e);
      }
    }
    
    setFormData(updatedFormData);
  };

  // Manejar cambio de gremio y cargar operarios activos del gremio seleccionado
  const handleGuildChange = async (guildOption) => {
    setFormData({ ...formData, guild: guildOption, worker: null }); // Resetear worker al cambiar gremio
    
    if (guildOption && guildOption.value) {
      try {
        console.log("Cargando operarios para gremio:", guildOption.value);
        const activeWorkers = await getActiveWorkersByGuild(guildOption.value);
        console.log("Operarios activos del gremio:", activeWorkers);
        
        setWorkerOptions(
          activeWorkers.map((worker) => ({ 
            value: worker.operarioId, 
            label: worker.nombre 
          }))
        );
      } catch (error) {
        console.error("Error loading workers by guild:", error);
        setWorkerOptions([]); // Limpiar opciones en caso de error
      }
    } else {
      // Si no hay gremio seleccionado, limpiar opciones de workers
      setWorkerOptions([]);
    }
  };

  // Reset form to initial state
  const resetForm = () => {
    setFormData({
      guild: null,
      worker: null,
      appointmentDate: "",
      endDate: "",
      closedDate: "",
      appointmentType: null,
      contacts: [],
      budgetNumber: "",
      workforce: "",
      materialWorker: "",
      material: "",
      information: "",
      observaciones: "",
      attachedFiles: [],
      cargarOperario: false,
    });
    setAttachedFiles([]);
    setIsReserved(false);
    setIsCancelled(false);
    setIsUrgent(false);
    setSinHora(false);
    setIsEditMode(false);
    setEditingAppointment(null);
  };

  // Cancel edit mode
  const handleCancelEdit = () => {
    resetForm();
  };

  const handleIsCancelledChange = (e) => {
    const checked = typeof e === "boolean" ? e : (e && e.target ? e.target.checked : false);
    setIsCancelled(checked);
  };

  const handleIsReservedChange = (e) => {
    const checked = typeof e === "boolean" ? e : (e && e.target ? e.target.checked : false);
    setIsReserved(checked);
  };

  const handleIsUrgentChange = (e) => {
    const checked = typeof e === "boolean" ? e : (e && e.target ? e.target.checked : false);
    setIsUrgent(checked);
  };

  const handleSinHoraChange = (e) => {
    const checked = typeof e === "boolean" ? e : (e && e.target ? e.target.checked : false);
    setSinHora(checked);
    
    // Si se marca 'Sin hora' y ya hay fechas seleccionadas, ajustarlas
    if (checked) {
      if (formData.appointmentDate) {
        const dateOnly = new Date(formData.appointmentDate);
        dateOnly.setHours(0, 0, 0, 0);
        setFormData(prev => ({ ...prev, appointmentDate: dateOnly }));
      }
      if (formData.endDate) {
        const dateOnly = new Date(formData.endDate);
        dateOnly.setHours(1, 0, 0, 0);
        setFormData(prev => ({ ...prev, endDate: dateOnly }));
      }
    }
  };

  // Open budget line modal
  const handleOpenBudgetLineModal = async () => {
    try {
      setLoading(true);
      const lines = await getBudgetLinesSummary(parseInt(recordData?.expedienteId.toString().replace(/\s+/g, '')));
      console.log("Budget lines loaded:", lines);
      setBudgetLines(lines);
      setIsBudgetLineModalOpen(true);
    } catch (error) {
      console.error("Error loading budget lines:", error);
      showErrorToast("Error al cargar las líneas de presupuesto");
    } finally {
      setLoading(false);
    }
  };

  // Handle budget line selection confirmation
  const handleBudgetLineConfirm = async (selectedLines) => {
    console.log("Selected budget lines:", selectedLines);
    
    try {
      setLoading(true);
      
      // Convertir el array de líneas seleccionadas a un string de IDs separados por coma
      const lineIds = selectedLines.map(line => line.id).join(',');
      console.log("Line IDs to fetch:", lineIds);
      
      // Obtener los detalles completos de las líneas desde la API
      const lineDetails = await getBudgetLineForAppointment(lineIds);
      console.log("Line details from API:", lineDetails);
      
      // Buscar el gremio en las opciones disponibles
      const guildOption = guildOptions.find(g => g.value === lineDetails.gremio.id);
      
      // Mapear tipoCita a appointmentType
      let appointmentTypeOption = null;
      if (lineDetails.tipoCita) {
        const appointmentTypes = [
          { value: "Visita", label: "Visita" },
          { value: "Realizar trabajo", label: "Realizar trabajo" },
          { value: "Reclamación", label: "Reclamación" },
          { value: "Mediciones", label: "Mediciones" },
          { value: "Fotos", label: "Fotos" },
        ];
        appointmentTypeOption = appointmentTypes.find(t => t.value === lineDetails.tipoCita);
        console.log("Tipo de cita encontrado:", appointmentTypeOption);
      }
      
      console.log("Guild option:", guildOption);
      console.log("Appointment type option:", appointmentTypeOption);
      console.log("Preparando datos del formulario:");
      console.log("- Gremio:", guildOption);
      console.log("- Tipo de cita:", appointmentTypeOption);
      console.log("- Número presupuesto:", lineDetails.numPresupuesto);
      console.log("- Mano de obra:", lineDetails.costeManoObraOperario);
      console.log("- Material operario:", lineDetails.costeMaterialOperario);
      console.log("- Material empresa:", lineDetails.costeMaterialEmpresa);
      console.log("- Información:", lineDetails.info ? lineDetails.info.substring(0, 50) + "..." : "");
      
      // Si hay un gremio, cargar sus operarios PRIMERO
      if (guildOption && guildOption.value) {
        try {
          console.log("Cargando operarios para gremio:", guildOption.value);
          const activeWorkers = await getActiveWorkersByGuild(guildOption.value);
          console.log("Operarios activos del gremio:", activeWorkers);
          
          setWorkerOptions(
            activeWorkers.map((worker) => ({ 
              value: worker.operarioId, 
              label: worker.nombre 
            }))
          );
        } catch (error) {
          console.error("Error loading workers by guild:", error);
          setWorkerOptions([]);
        }
      }
      
      // Actualizar el formulario con TODOS los datos de la API
      const updatedFormData = {
        ...formData,
        guild: guildOption || null,
        worker: null, // Resetear worker al cambiar gremio
        appointmentType: appointmentTypeOption || null,
        budgetNumber: lineDetails.numPresupuesto !== null && lineDetails.numPresupuesto !== undefined ? lineDetails.numPresupuesto.toString() : "",
        workforce: lineDetails.costeManoObraOperario !== null && lineDetails.costeManoObraOperario !== undefined ? lineDetails.costeManoObraOperario.toString() : "",
        materialWorker: lineDetails.costeMaterialOperario !== null && lineDetails.costeMaterialOperario !== undefined ? lineDetails.costeMaterialOperario.toString() : "",
        material: lineDetails.costeMaterialEmpresa !== null && lineDetails.costeMaterialEmpresa !== undefined ? lineDetails.costeMaterialEmpresa.toString() : "",
        information: lineDetails.info || "",
      };
      
      console.log("Datos del formulario actualizados:", updatedFormData);
      setFormData(updatedFormData);
      
      const lineCount = selectedLines.length;
      const successMessage = lineCount === 1 
        ? `Línea de presupuesto cargada: ${lineDetails.gremio.nombre}`
        : `${lineCount} líneas de presupuesto cargadas: ${lineDetails.gremio.nombre}`;
      showSuccessToast(successMessage);
    } catch (error) {
      console.error("Error loading budget line details:", error);
      showErrorToast("Error al cargar los detalles de la línea de presupuesto");
    } finally {
      setLoading(false);
      setIsBudgetLineModalOpen(false);
    }
  };

  // Handle budget line modal cancel
  const handleBudgetLineCancel = () => {
    setIsBudgetLineModalOpen(false);
  };

  // Open send appointment modal
  const handleOpenSendModal = (appointmentId) => {
    console.log("Opening send modal for appointment:", appointmentId);
    setSelectedAppointmentId(appointmentId);
    setIsSendModalOpen(true);
  };

  // Handle appointment send
  const handleSendAppointment = async (sendData) => {
    try {
      setLoading(true);
      console.log("Sending appointment with data:", sendData);
      
      await sendAppointment(selectedAppointmentId, sendData);
      showSuccessToast("Cita enviada correctamente");
      
      // Close modal
      setIsSendModalOpen(false);
      setSelectedAppointmentId(null);
    } catch (error) {
      console.error("Error sending appointment:", error);
      const errorMessage = error?.response?.data?.message || error?.message || "Error al enviar la cita";
      showErrorToast(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Handle send modal cancel
  const handleSendCancel = () => {
    setIsSendModalOpen(false);
    setSelectedAppointmentId(null);
  };

  // Samba file picker handlers
  const handleSambaFilesSelect = (filesData) => {
    // filesData is an array of objects {path, name, size}
    // Extract only the paths as strings
    const newPaths = (filesData || []).map(file => {
      // If it's already a string, return it
      if (typeof file === 'string') return file;
      // If it's an object with path property, return the path
      if (file && typeof file === 'object' && file.path) return file.path;
      // Otherwise return empty string
      return '';
    }).filter(path => path); // Remove empty strings
    
    // Remover duplicados: crear un Set con todos los paths (existentes + nuevos)
    const allPaths = [...new Set([...newPaths])];
    
    console.log('Archivos seleccionados (paths):', allPaths);
    setAttachedFiles(allPaths);
    setFormData(prev => ({ ...prev, attachedFiles: allPaths }));
    setIsSambaOpen(false);
  };

  // Remove attached file handler
  const handleRemoveAttachedFile = (pathToRemove) => {
    const updatedFiles = attachedFiles.filter(path => path !== pathToRemove);
    setAttachedFiles(updatedFiles);
    setFormData(prev => ({ ...prev, attachedFiles: updatedFiles }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validación: asegurar que se seleccione un operario
    if (!formData.worker || !formData.worker.value) {
      showErrorToast("Por favor selecciona un operario");
      return;
    }

    // Validación: asegurar que se ingrese la fecha de fin
    if (!formData.endDate) {
      showErrorToast("Por favor ingresa la fecha de fin");
      return;
    }

    console.log("Form data before submit:", formData);
    console.log("Selected worker:", formData.worker);

    // Helper: strip SAMBA prefix from file paths
    const stripSambaPrefix = (path) => {
      const prefix = '/srv/samba/shared/Expedientes/';
      if (path && path.startsWith(prefix)) {
        return path.substring(prefix.length);
      }
      return path;
    };

    const appointmentData = {
      gremioId: formData.guild?.value || null,
      operarioId: formData.worker?.value || null,
      fechaCita: convertLocalToISO(formData.appointmentDate),
      fechaCitaFin: convertLocalToISO(formData.endDate),
      fechaFin: convertLocalToISO(formData.closedDate),
      tipoCita: formData.appointmentType?.label || null,
      expedienteId: parseInt(expedienteId.toString().replace(/\s+/g, '')) || null,
      contactosIds:
        formData.contacts && formData.contacts.length > 0
          ? formData.contacts.map((c) => c.value)
          : [],
      estadoCita: "RECIBIDA", // Campo requerido por la API
      numPresupuesto: parseInt(formData.budgetNumber, 10) || null,
      costeManoObraOperario: parseFloat(formData.workforce) || 0,
      costeMaterialOperario: parseFloat(formData.materialWorker) || 0,
      costeMaterialEmpresa: parseFloat(formData.material) || 0, // Cambio de nombre
      costeTotal: parseFloat(calculateTotalCost()) || 0,
      info: formData.information || "",
      // Enviar tanto `cargarOperario` como `cargado` para compatibilidad con el backend
      cargarOperario: formData.cargarOperario || false,
      cargado: formData.cargarOperario || false,
      // Checkboxes and observations added
      reserva: !!isReserved,
      cancelada: !!isCancelled,
      urgente: !!isUrgent,
      observaciones: formData.observaciones || "",
      // Campos booleanos requeridos por la API
      tieneFotos: false,
      tieneFirmas: false,
      tienePresupuesto: false,
      tieneComentarios: false,
      // Archivos visibles seleccionados desde SAMBA - strip prefix
      archivosVisiblesRel: (attachedFiles || []).map(stripSambaPrefix)
    };

    console.log("Appointment Data to submit:", appointmentData);
    try {
      setLoading(true);
      let response;

      if (isEditMode && editingAppointment) {
        // Actualizar cita existente
        response = await updateAppointment(
          editingAppointment.id,
          appointmentData
        );
        console.log("Appointment updated successfully:", response);
        showSuccessToast(notifications.update.success("Cita"));
        // Refetch statistics after updating appointment
        if (refetchStatistics) refetchStatistics();
      } else {
        // Crear nueva cita
        response = await createAppointment(appointmentData);
        console.log("Appointment created successfully:", response);
        showSuccessToast(notifications.create.success("Cita"));
        
        // Refetch statistics after creating appointment
        if (refetchStatistics) refetchStatistics();
      }

      // Refresh appointments table
      const appointments = await getAppointmentSummary(expedienteId);
      console.log("Refreshing appointments data:", appointments);
      
      let formattedAppointments = [];
      if (Array.isArray(appointments)) {
        formattedAppointments = appointments.map((appointment) => {
          const fechaCitaDate = appointment.fechaCita ? new Date(appointment.fechaCita) : null;
          const fechaCitaStr = fechaCitaDate
            ? (fechaCitaDate.getHours() === 0 && fechaCitaDate.getMinutes() === 0
              ? format(fechaCitaDate, "dd/MM/yyyy") + " - Sin hora"
              : format(fechaCitaDate, "dd/MM/yyyy - HH:mm"))
            : "N/A";
          
          return {
            ...appointment,
            fechaCita: fechaCitaStr,
            fechaFin: appointment.fechaFin ? format(
                new Date(appointment.fechaFin),
                "dd/MM/yyyy - HH:mm"
              ) : "N/A",
            // Formatear coste con símbolo de euro y añadir etiqueta "- Cargado" si viene marcado
            costeTotal: appointment.costeTotal != null
              ? (appointment.costeTotal.toLocaleString("es-ES", { style: "currency", currency: "EUR" }) + ((appointment.cargado || appointment.cargarOperario) ? ' - Cargado' : ''))
              : "",
          };
        });
      } else {
        console.log("No appointments found or wrong data format");
        formattedAppointments = [];
      }
      
      setAppointmentsTableData(formattedAppointments);

      // Reset form and edit mode
      resetForm();
      // Resetear estado de cambios sin guardar
      setInitialFormData(null);
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error(
        isEditMode
          ? "Error updating appointment:"
          : "Error creating appointment:",
        error
      );
      showErrorToast(
        isEditMode
          ? notifications.update.error("la cita")
          : notifications.create.error("la cita")
      );
    } finally {
      setLoading(false);
    }
  };

  const appointmentTypeOptions = [
    { value: "Visita", label: "Visita" },
    { value: "Realizar trabajo", label: "Realizar trabajo" },
    { value: "Reclamación", label: "Reclamación" },
    { value: "Mediciones", label: "Mediciones" },
    { value: "Fotos", label: "Fotos" },
  ];

  const appointmentsTableHeaders = [
    { label: "Fecha Inicio", key: "fechaCita" },
    { label: "Fecha Cerrado", key: "fechaFin" },
    { label: "Gremio", key: "gremio" },
    { label: "Operario", key: "operarioNombre" },
    { label: "Coste", key: "costeTotal" },
    { label: "Estado de cita", key: "estadoCita" },
    { label: "Nº Presupuesto", key: "numPresupuesto" },
  ];

  const handleAppointmentRowClick = async (appointment) => {
    try {
      const cita = await getAppointmentById(appointment.id);
      console.log("Cita encontrada:", cita);
      
      // Si hay contactosIds, obtener los contactos completos
      let contactsFormatted = [];
      if (cita.contactosIds && cita.contactosIds.length > 0) {
        // Usar contactsOptions que ya tiene todos los contactos formateados
        contactsFormatted = cita.contactosIds.map(contactId => {
          const contact = contactsOptions.find(c => c.value === contactId);
          return contact || null;
        }).filter(Boolean);
      }
      
      // Preparar el gremio
      const guildFormatted = cita.gremio || (cita.gremioId && guildOptions.find(g => g.value === cita.gremioId))
        ? { value: cita.gremio?.id || cita.gremioId, label: cita.gremio?.nombre || guildOptions.find(g => g.value === cita.gremioId)?.label }
        : null;
      
      // Preparar archivos adjuntos si existen (reconstruir rutas SAMBA completas)
      let attachedFilesFormatted = [];
      if (cita.archivosVisiblesRel && cita.archivosVisiblesRel.length > 0) {
        // archivosVisiblesRel viene como array de rutas relativas: ["2025/250596/carpeta/archivo.txt"]
        // Necesitamos convertirlas a rutas SAMBA completas
        attachedFilesFormatted = cita.archivosVisiblesRel.map(relativePath => {
          return `/srv/samba/shared/Expedientes/${relativePath}`;
        });
        console.log("Archivos adjuntos cargados:", attachedFilesFormatted);
      }
      
      // Calcular estado de checkboxes y sinHora a partir de los datos de la cita
      const isReservedValue = !!(cita.reserva);
      const isCancelledValue = !!(cita.cancelada);
      const isUrgentValue = !!(cita.urgente);
      const fechaCitaDate = convertISOToLocal(cita.fechaCita);
      const fechaFinDate = convertISOToLocal(cita.fechaCitaFin);
      const isSinHoraValue = fechaCitaDate && fechaFinDate
        ? (fechaCitaDate.getHours() === 0 && fechaCitaDate.getMinutes() === 0 &&
           fechaFinDate.getHours() === 1 && fechaFinDate.getMinutes() === 0)
        : false;

      // Cargar operarios del gremio para poder resolver el worker antes de setear el estado
      let finalWorker = null;
      if (guildFormatted && guildFormatted.value) {
        try {
          const workers = await getActiveWorkersByGuild(guildFormatted.value);
          const formattedWorkers = workers.map(worker => ({
            value: worker.operarioId,
            label: worker.nombre
          }));
          setWorkerOptions(formattedWorkers);
          if (cita.operarioId) {
            finalWorker = formattedWorkers.find(w => w.value === cita.operarioId) || null;
          }
        } catch (error) {
          console.error("Error loading workers:", error);
          setWorkerOptions([]);
        }
      }

      // Construir el estado final completo del formulario (con worker ya resuelto)
      const finalFormState = {
        guild: guildFormatted,
        worker: finalWorker,
        appointmentDate: convertISOToLocal(cita.fechaCita),
        endDate: convertISOToLocal(cita.fechaCitaFin),
        closedDate: convertISOToLocal(cita.fechaFin),
        appointmentType: cita.tipoCita
          ? { value: cita.tipoCita, label: cita.tipoCita }
          : null,
        contacts: contactsFormatted,
        budgetNumber: cita.numPresupuesto != null ? String(cita.numPresupuesto) : "",
        workforce: cita.costeManoObraOperario != null ? String(cita.costeManoObraOperario) : "",
        materialWorker: cita.costeMaterialOperario != null ? String(cita.costeMaterialOperario) : "",
        material: cita.costeMaterialEmpresa != null ? String(cita.costeMaterialEmpresa) : "",
        information: cita.info != null ? String(cita.info) : "",
        observaciones: cita.observaciones != null ? String(cita.observaciones) : "",
        cargarOperario: (cita.cargarOperario !== undefined ? cita.cargarOperario : (cita.cargado !== undefined ? cita.cargado : false)),
        attachedFiles: attachedFilesFormatted,
      };

      // Setear todo el estado de una sola vez
      setFormData(finalFormState);
      setAttachedFiles(attachedFilesFormatted);
      setIsEditMode(true);
      setEditingAppointment(cita);
      setIsReserved(isReservedValue);
      setIsCancelled(isCancelledValue);
      setIsUrgent(isUrgentValue);
      setSinHora(isSinHoraValue);

      // Guardar estado inicial para que la detección de cambios no genere falsos positivos
      setInitialFormData({
        ...finalFormState,
        initialAttachedFiles: attachedFilesFormatted,
        isReserved: isReservedValue,
        isCancelled: isCancelledValue,
        isUrgent: isUrgentValue,
      });
    } catch (error) {
      showErrorToast("No se pudo cargar la cita para editar");
      console.error(error);
    }
  };
  // Calcular el coste total
  const calculateTotalCost = () => {
    const workforce = parseFloat(formData.workforce) || 0;
    const materialWorker = parseFloat(formData.materialWorker) || 0;
    const material = parseFloat(formData.material) || 0;

    const total = workforce + materialWorker + material;
    return total.toFixed(2);
  };

  // useEffect para detectar cambios sin guardar
  useEffect(() => {
    if (!initialFormData) {
      // Si no hay datos iniciales aún, no hay cambios
      setHasUnsavedChanges(false);
      return;
    }

    // Extraer campos extra de initialFormData para comparar solo los campos del formulario
    const { initialAttachedFiles, isReserved: initIsReserved, isCancelled: initIsCancelled, isUrgent: initIsUrgent, ...initialFormOnly } = initialFormData;
    const formChanged = JSON.stringify(formData) !== JSON.stringify(initialFormOnly);
    const filesChanged = JSON.stringify(attachedFiles) !== JSON.stringify(initialAttachedFiles || []);
    const checkboxesChanged = (isReserved !== initIsReserved) || 
                              (isCancelled !== initIsCancelled) || 
                              (isUrgent !== initIsUrgent);

    setHasUnsavedChanges(formChanged || filesChanged || checkboxesChanged);
  }, [formData, attachedFiles, isReserved, isCancelled, isUrgent, initialFormData]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch guilds
        const guilds = await getGuilds();
        setGuildOptions(
          guilds.map((item) => ({ value: item.id, label: item.nombre }))
        );

        // No cargar todos los workers inicialmente - se cargarán al seleccionar un gremio
        setWorkerOptions([]);

        // Fetch contacts
        const contacts = await getContactsByExpedienteId(parseInt(expedienteId.toString().replace(/\s+/g, '')));
        const contactsFormatted = contacts.map((c) => ({
          value: c.id,
          label: `${c.nombre}${c.contactoRol ? ` - ${c.contactoRol}` : ""}${
            c.telefono ? ` - ${c.telefono}` : ""
          }`,
        }));
        
        setContactsOptions(contactsFormatted);
        
        // Preseleccionar todos los contactos solo si no estamos en modo edición
        if (!isEditMode) {
          setFormData(prev => ({
            ...prev,
            contacts: contactsFormatted
          }));
          // Guardar estado inicial
          setInitialFormData({
            ...formData,
            contacts: contactsFormatted,
            initialAttachedFiles: [],
            isReserved: false,
            isCancelled: false,
            isUrgent: false
          });
        }

        // Fetch appointments
        const appointments = await getAppointmentSummary(parseInt(expedienteId.toString().replace(/\s+/g, '')));
        console.log("Expediente ID:", expedienteId);
        console.log("Fetched appointments data:", appointments);
        
        // Verificar si appointments es un array o un objeto
        let formattedAppointments = [];
        if (Array.isArray(appointments)) {
          // Si es un array de citas, formatear cada una
          formattedAppointments = appointments.map((appointment) => {
            const fechaCitaDate = appointment.fechaCita ? new Date(appointment.fechaCita) : null;
            const fechaCitaStr = fechaCitaDate
              ? (fechaCitaDate.getHours() === 0 && fechaCitaDate.getMinutes() === 0
                ? format(fechaCitaDate, "dd/MM/yyyy") + " - Sin hora"
                : format(fechaCitaDate, "dd/MM/yyyy - HH:mm"))
              : "N/A";
            
            return {
              ...appointment,
              fechaCita: fechaCitaStr,
              fechaFin: appointment.fechaFin ? format(
                new Date(appointment.fechaFin),
                "dd/MM/yyyy - HH:mm"
              ) : "N/A",
              // Formatear coste con símbolo de euro y añadir etiqueta "- Cargado" si viene marcado
              costeTotal: appointment.costeTotal != null
                ? (appointment.costeTotal.toLocaleString("es-ES", { style: "currency", currency: "EUR" }) + ((appointment.cargado || appointment.cargarOperario) ? ' - Cargado' : ''))
                : "",
            };
          });
        } else if (appointments && typeof appointments === 'object') {
          // Si es un objeto (datos del expediente), crear array vacío
          console.log("getAppointmentSummary devolvió datos del expediente, no citas");
          formattedAppointments = [];
        }
        
        console.log("Formatted appointments for table:", formattedAppointments);
        setAppointmentsTableData(formattedAppointments);
        
        // Debug: Log table data after setting
        console.log("Table data set to:", formattedAppointments);
      } catch (error) {
        console.error("Error getting appointment:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [expedienteId, isEditMode]);

  return (
    <div style={{ paddingTop: "24px" }}>
      <form onSubmit={handleSubmit} style={{ marginBottom: "24px" }}>
        <div
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "row",
            gap: "12px",
          }}
        >
          <div ref={leftColumnRef} style={{ flex: 1 }}>
            <FormSection title="General" style={{ marginBottom: "12px"}}  renderRight={() => (
                <div className={styles.checkboxContainer}>
                  <Checkbox
                    label="Reservada"
                    checked={isReserved}
                    onChange={handleIsReservedChange}
                  />
                  <Checkbox
                    label="Cancelada"
                    checked={isCancelled}
                    onChange={handleIsCancelledChange}
                  />
                  <Checkbox
                    label="Urgente"
                    checked={isUrgent}
                    onChange={handleIsUrgentChange}
                  />
                  
                </div>
              )}
            >
              <div className={styles.sectionRow} style={{ marginBottom: "0px" }}>
                <Dropdown
                  label={"Gremio"}
                  options={guildOptions}
                  onSelect={(option) => handleGuildChange(option)}
                  style={{ marginTop: "3px", width: "100%" }}
                  mainStyle={{ flex: 1 }}
                  selected={formData.guild}
                />

                <Dropdown
                  label={"Operario"}
                  options={workerOptions}
                  onSelect={(option) => handleDropdownSelect("worker", option)}
                  style={{ marginTop: "3px", width: "100%" }}
                  mainStyle={{ flex: 1 }}
                  selected={formData.worker}
                />

                <Dropdown
                  label={"Tipo de cita"}
                  options={appointmentTypeOptions}
                  onSelect={(option) =>
                    handleDropdownSelect("appointmentType", option)
                  }
                  style={{ marginTop: "3px", width: "100%" }}
                  mainStyle={{ flex: 1 }}
                  selected={formData.appointmentType}
                />

                <Dropdown
                  label={"Contactos"}
                  options={contactsOptions}
                  onSelect={(option) =>
                    handleDropdownSelect("contacts", option)
                  }
                  style={{ marginTop: "3px", width: "100%" }}
                  mainStyle={{ flex: 1 }}
                  selected={formData.contacts}
                  multiSelect={true}
                />
              </div>

              <div className={`${styles.sectionRow} ${styles.fullExpand}`}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                  <DateTimePicker
                    ref={appointmentRef}
                    label="Fecha de la cita"
                    selected={formData.appointmentDate}
                    onChange={(date) => handleDateChange('appointmentDate', date)}
                    placeholder="Seleccionar fecha y hora"
                    style={{ flex: 1 }}
                    dateOnly={sinHora}
                  />
                  <div style={{ display: 'flex', alignItems: 'center', marginTop: '24px' }}>
                    <Checkbox
                      label="Sin hora"
                      checked={sinHora}
                      onChange={handleSinHoraChange}
                    />
                  </div>
                </div>

                <DateTimePicker
                  ref={endRef}
                  label="Fecha de fin"
                  selected={formData.endDate}
                  onChange={(date) => handleDateChange('endDate', date)}
                  placeholder="Seleccionar fecha y hora"
                  style={{ flex: 1 }}
                  dateOnly={sinHora}
                />

                <DateTimePicker
                  ref={closedRef}
                  label="Fecha de cerrado"
                  selected={formData.closedDate}
                  onChange={(date) => handleDateChange('closedDate', date)}
                  placeholder="Seleccionar fecha y hora"
                  style={{ flex: 1 }}
                />
              </div>
            </FormSection>
            <FormSection 
              title="Costes"
              renderRight={() => {
                const totalCost = parseFloat(calculateTotalCost()) || 0;
                const hasClosedDate = !!formData.closedDate;
                const shouldShowCheckbox = hasClosedDate && totalCost !== 0;
                
                return shouldShowCheckbox ? (
                  <Checkbox
                    label="Cargar al operario"
                    checked={formData.cargarOperario}
                    onChange={(checked) => setFormData({ ...formData, cargarOperario: checked })}
                    disabled={!!isLockedByOther}
                  />
                ) : null;
              }}
            >
              <div className={styles.sectionRow}>
                <Input
                  label="Nº de presupuesto"
                  name="budgetNumber"
                  placeholder={"Numero"}
                  value={formData.budgetNumber}
                  onChange={handleInputChange}
                  style={{ flex: 1 }}
                />
                <Input
                  label="Mano obra"
                  name="workforce"
                  placeholder={"Coste"}
                  value={formData.workforce}
                  onChange={handleInputChange}
                  style={{ flex: 1 }}
                />
                <Input
                  label="Material operario"
                  name="materialWorker"
                  placeholder={"Coste"}
                  value={formData.materialWorker}
                  onChange={handleInputChange}
                  style={{ flex: 1 }}
                />
                <Input
                  label="Material empresa"
                  name="material"
                  placeholder={"Coste"}
                  value={formData.material}
                  onChange={handleInputChange}
                  style={{ flex: 1 }}
                />

                <div className={styles.resultContainer}>
                  <MyIcons name="equal" width={24} height={24} />

                  <div className={styles.resultTextContainer}>
                    <span className={styles.resultText}>Coste total</span>
                    <span className={styles.resultValueText}>
                      {calculateTotalCost()} €
                    </span>
                  </div>
                </div>
              </div>
            </FormSection>
          </div>

          {/* Right column: Observaciones (small box) */}
          <div ref={obsContainerRef} style={{ minWidth: "300px", width: "320px", display: "flex", flex: '0 0 320px', flexDirection: "column", gap: "12px", alignItems: 'stretch' }}>
            <FormSection title="Observaciones" style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
              <textarea
                name="observaciones"
                placeholder="Observaciones"
                value={formData.observaciones || ""}
                onChange={(e) => setFormData({ ...formData, observaciones: e.target.value })}
                className={styles.workTextarea}
                style={{
                  width: "100%",
                  flex: 1,
                  padding: "12px",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  fontFamily: "var(--font-roboto)",
                  fontSize: "14px",
                  color: "black",
                  resize: "vertical",
                  outline: "none",
                }}
                spellCheck="true"
              />
            </FormSection>
          </div>
        </div>
        {/* New row: Información (wide) + Añadir archivos (narrow) */}
        <div className={styles.sectionRow} style={{ marginTop: "12px" }}>
          <FormSection title="Información" style={{ flex: 1 }}>
            <textarea
              name="information"
              placeholder="Información adicional sobre la cita"
              value={formData.information}
              onChange={handleInputChange}
              className={styles.workTextarea}
              style={{
                width: "100%",
                height: "120px",
                padding: "12px",
                border: "1px solid #ddd",
                borderRadius: "4px",
                fontFamily: "var(--font-roboto)",
                fontSize: "14px",
                color: "black",
                resize: "none",
                outline: "none",
                whiteSpace: "pre-wrap",
                wordWrap: "break-word",
                overflow: "auto",
              }}
              spellCheck="true"
            />
          </FormSection>

          <FormSection title="Archivos adjuntos" style={{ minWidth: "300px", width: "320px", flex: '0 0 320px', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '150px', overflowY: 'auto' }}>
              {attachedFiles && attachedFiles.length > 0 ? (
                attachedFiles.map((filePath, index) => {
                  // Validar que filePath es un string antes de hacer split
                  if (!filePath || typeof filePath !== 'string') {
                    return null;
                  }
                  const fileName = filePath.split('/').pop();
                  return (
                    <div key={index} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 8px', background: '#f5f5f5', borderRadius: '4px' }}>
                      <span style={{ fontSize: '12px', color: 'var(--dark-grey)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }} title={filePath}>{fileName}</span>
                      <div 
                        onClick={() => handleRemoveAttachedFile(filePath)}
                        style={{ cursor: 'pointer', marginLeft: '8px', display: 'flex', alignItems: 'center', padding: '2px' }}
                      >
                        <MyIcons name="trash" width={16} height={16} color="#dc3545" />
                      </div>
                    </div>
                  );
                })
              ) : (
                <div style={{ textAlign: 'center', color: 'var(--dark-grey)', fontSize: '12px', padding: '12px 0' }}>
                  Sin archivos adjuntos
                </div>
              )}
            </div>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setIsSambaOpen(true)}
              style={{ marginTop: '8px', width: '100%' }}
            >
              + Añadir archivos
            </Button>
          </FormSection>
        </div>

        <div className={styles.buttonsContainer}>
          <Button type="submit" disabled={loading || isLockedByOther}>
            {isEditMode ? "GUARDAR" : "CREAR CITA"}
          </Button>
          {isEditMode && (
            <Button
              type="button"
              variant="secondary"
              onClick={handleCancelEdit}
            >
              CANCELAR
            </Button>
          )}
          {!isEditMode && (
            <Button 
              type="button" 
              variant="secondary"
              onClick={handleOpenBudgetLineModal}
              disabled={loading || isLockedByOther}
            >
              CREAR DESDE LINEA
            </Button>
          )}
        </div>
      </form>
      <Table
        headers={appointmentsTableHeaders}
        data={appointmentsTableData}
        onPress={isLockedByOther ? null : handleAppointmentRowClick}
        renderActions={(row) => (
          <div className={styles.actionButtons}>
            <div
              onClick={() => handleOpenSendModal(row.id)}
              style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <MyIcons
                name="send"
                width={24}
                height={24}
                color={"var(--main)"}
              />
            </div>
          </div>
        )}
      />
      
      {/* Modal de selección de líneas de presupuesto */}
      <BudgetLineSelectionModal
        open={isBudgetLineModalOpen}
        budgetLines={budgetLines}
        onConfirm={handleBudgetLineConfirm}
        onCancel={handleBudgetLineCancel}
      />
      
      {/* Modal de envío de cita */}
      <SendAppointmentModal
        open={isSendModalOpen}
        appointmentId={selectedAppointmentId}
        expedienteId={parseInt(expedienteId.toString().replace(/\s+/g, ''))}
        onSend={handleSendAppointment}
        onCancel={handleSendCancel}
      />
      {/* Samba file picker modal used across the app */}

      {/* Modal de advertencia de cambios sin guardar */}
      <UnsavedChangesModal
        isOpen={showModal}
        onCancel={handleCancel}
        onConfirm={handleConfirm}
        message={message}
      />
      <SambaFilePickerModal
        isOpen={isSambaOpen}
        onClose={() => setIsSambaOpen(false)}
        onFilesSelect={handleSambaFilesSelect}
        expId={parseInt(expedienteId.toString().replace(/\s+/g, ''))}
        expedienteYear={Math.floor(parseInt(expedienteId.toString().replace(/\s+/g, '')) / 10000) + 2000}
        multiple={true}
        initialSelectedFiles={attachedFiles}
      />
    </div>
  );
};

export default Appointments;
